import React, { useMemo } from 'react';
import Select, {
  SingleValue,
  MultiValue,
  components,
  NoticeProps,
  GroupBase
} from 'react-select';
import { IOption } from '../../core/_models';
import { envConfig } from '../../../../utils/config';

interface CustomFormSelectProps {
  name?: string;
  value: string | number | (string | number)[] | null;
  onChange: (value: string | number | (string | number)[] | null) => void;
  options: IOption[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  isMulti?: boolean;
  size?: 'lg' | 'md' | 'sm';
  usePortal?: boolean;
  portalTarget?: HTMLElement | null;
  portalZIndex?: number;
  isClearable?: boolean;
  enableSelectAll?: boolean;
  onAddNew?: () => void;
  addNewLabel?: string;
}

const SELECT_ALL_VALUE = '__select_all__';

const CustomFormSearchSelect: React.FC<CustomFormSelectProps> = ({
  name,
  value,
  onChange,
  options,
  disabled = false,
  size = envConfig.DEFAULT_INPUT_FIELD_SIZE,
  placeholder = 'Select an option',
  className = `form-select-custom-${size}`,
  isMulti = false,
  usePortal = false,
  portalTarget = typeof document !== 'undefined' ? document.body : null,
  portalZIndex = 9999,
  isClearable = false,
  enableSelectAll = false,
  onAddNew,
  addNewLabel = '+ Add Contractor',
}) => {
  const selectAllOption: IOption = {
    label: 'Select All',
    value: SELECT_ALL_VALUE,
  };

  const finalOptions = useMemo(() => {
    let opts = [...options];

    if (isMulti && enableSelectAll) {
      opts = [selectAllOption, ...opts];
    }

    return opts;
  }, [options, isMulti, enableSelectAll]);

  // Map selected value(s)
  const selectedOption = useMemo(() => {
    if (isMulti) {
      return options.filter(opt => Array.isArray(value) && value.includes(opt.value));
    }
    return options.find(opt => opt.value === value) || null;
  }, [options, value, isMulti]);

  const allEnabledValues = useMemo(() =>
    options
      .filter(opt => !opt.isDisabled)
      .map(opt => opt.value),
    [options]
  );

  // Custom NoOptionsMessage component
  const CustomNoOptionsMessage = (props: NoticeProps<IOption, boolean, GroupBase<IOption>>) => {
    return (
      <components.NoOptionsMessage {...props}>
        <div
          className="metronic-select-add-new-option text-hover-primary"
          style={{ cursor: 'pointer', padding: '8px 12px' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddNew?.();
          }}
        >
          {addNewLabel}
        </div>
      </components.NoOptionsMessage>
    );
  };

  // Handle change
  const handleChange = (newValue: MultiValue<IOption> | SingleValue<IOption>) => {
    if (isMulti) {
      const selected = newValue as MultiValue<IOption>;
      const values = selected.map(opt => opt.value);

      if (values.includes(SELECT_ALL_VALUE)) {
        const isAllSelected = Array.isArray(value) &&
          allEnabledValues.every(v => value.includes(v));

        onChange(isAllSelected ? [] : allEnabledValues);
        return;
      }

      onChange(values.length ? values : null);
    } else {
      onChange((newValue as SingleValue<IOption>)?.value ?? null);
    }
  };

  return (
    <Select
      name={name}
      value={selectedOption}
      options={finalOptions}
      isDisabled={disabled}
      placeholder={placeholder}
      className={className}
      classNamePrefix="metronic-select"
      isMulti={isMulti}
      isClearable={isClearable}
      menuPortalTarget={usePortal && portalTarget ? portalTarget : undefined}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: portalZIndex,
        }),
      }}
      components={onAddNew ? {
        NoOptionsMessage: CustomNoOptionsMessage
      } : undefined}
      onChange={handleChange}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
    />
  );
};

export default CustomFormSearchSelect;